import { useState, useMemo, useCallback, useRef, useEffect, Suspense, type CSSProperties } from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import { OrbitControls, Environment, Html, ContactShadows, Line } from '@react-three/drei'
import * as THREE from 'three'
import store from '@/store'

import {
  AppObject,
  LogoPlacement,
  LoadProgress,
  ModelUnit,
  MODEL_UNIT_MM,
  lengthUnits,
  roundLength,
  UvProjection,
  MaterialProperties,
  defaultMaterialProperties,
} from './types'
import { Download, LayoutTemplate, Plus, Redo2, RotateCcw, Ruler, Undo2 } from 'lucide-react'
import { FileUploadBox, ThreeObjectMesh, MaterialSettingsPanel, ObjectTree, SplitHeightPanel } from './components'
import { Checkbox, ToolButton, ToolDivider } from './components/ui'
import { loadSTLFiles, loadSTEPFile, loadGLBFile } from './loaders'
import { MeasurePoint } from './utils/measureSnap'
import {
  exportAsZip,
  exportAsSingleGLB,
  exportAsSTL,
  generatePatternTemplate,
  downloadDataUrl,
  applyCylindricalPatternUVs,
  findLogoObject,
  objectCenter,
  splitObjectByConnectivity,
  joinObjects,
  prepareLogoArtwork,
  ensureLogoOutline,
  buildLogoObject,
  defaultLogoPlacement,
  placementFromPoint,
  nextLogoId,
  logoTargetOf,
  surfaceFieldOf,
  radiusAt,
  splitObjectByHeight,
  heightRangeOf,
} from './utils'

/**
 * Re-bake cylindrical UVs to match the Product Profile wrap shader and anchor the
 * front-center (u=0.5) to `logo_main`. This un-mirrors the pattern, aligns it with
 * the model's logo, and — for UV-less GLBs — provides UVs to export. Only applied
 * for cylindrical (the projection used for bottles/tumblers).
 */
function bakePatternUVs(objects: AppObject[], projection: UvProjection): void {
  if (projection !== 'cylindrical') return
  // Logo sheets are excluded: this bakes a model-wide cylindrical wrap into every part's
  // UVs, and a logo's UVs are its artwork's own 0..1 mapping. Rewriting them wraps the
  // artwork around the whole model instead of sitting it on the sheet.
  applyCylindricalPatternUVs(
    objects.filter((o) => !o.logo),
    findLogoObject(objects)
  )
}

/**
 * Move the camera to a requested spot. Used to look straight at a logo: a level,
 * on-axis view is the only one that shows the artwork undistorted, because a logo
 * wrapped around a cup reads as tilted from anywhere else (its ends sit several
 * millimetres further from the eye than its middle).
 */
function CameraFocus({ request }: { request: { position: THREE.Vector3; nonce: number } | null }) {
  const camera = useThree((state) => state.camera)
  const controls = useThree((state) => state.controls) as { update?: () => void } | null
  useEffect(() => {
    if (!request) return
    camera.position.copy(request.position)
    controls?.update?.()
  }, [request, camera, controls])
  return null
}

/**
 * One undoable state. Snapshots share the (immutable) geometry — only the array
 * structure, part ids, and materials differ — so keeping many of them is cheap.
 */
interface HistorySnapshot {
  objects: AppObject[]
  globalMaterial: MaterialProperties
  selectedIds: string[]
}

type SelectMode = 'replace' | 'toggle' | 'range'

/** primary-500 — selection and guides read the same here as anywhere in the portal. */
const ACCENT = '#79BC61'
/**
 * gray-900 core on a white halo — measurement is neutral, so it is never mistaken for a
 * selection, and the pair reads on any surface: the core on a light part, the halo on a
 * black one. A single dark line disappeared against the black products this is used on.
 */
const MEASURE = '#141A12'
const MEASURE_HALO = '#FFFFFF'

/**
 * Measurement overlays must never take the pointer. A marker sits exactly under the cursor
 * (the hover marker follows it, and a free start point is where the press was), and R3F
 * aims its ray with the event's offsetX/Y — measured from whatever element was hit. A press
 * that landed on an 8px marker was cast from the canvas's top-left corner, missed the model
 * and never started a measurement. drei's `pointerEvents` prop only applies with
 * `transform`, so the style is set on the wrapper directly; the markers inherit it.
 */
const MEASURE_OVERLAY_STYLE: CSSProperties = { pointerEvents: 'none' }

/** Endpoint of a measurement: a square when snapped to a vertex, a dot when free. */
function MeasureMarker({ snapped }: { snapped: boolean }) {
  return (
    <div
      className={`${
        snapped ? 'w-[10px] h-[10px] rounded-[2px]' : 'w-[8px] h-[8px] rounded-full'
      } bg-gray-900 border-2 border-white`}
      style={{ boxShadow: '0 0 0 1px rgba(20,26,18,0.6)' }}
    />
  )
}

function StepToGlbConverter() {
  const [objects, setObjects] = useState<AppObject[]>([])
  // Multi-selection. The "primary" (active) part — used by the material/position
  // panel — is the most recently added id.
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const selectedObjectId = selectedIds.length ? selectedIds[selectedIds.length - 1] : null
  const [isExporting, setIsExporting] = useState(false)
  const [showExportOptions, setShowExportOptions] = useState(false)
  const [modelCenter, setModelCenter] = useState<THREE.Vector3 | null>(null)
  // What one model coordinate means. Everything metric follows from it.
  const [modelUnit, setModelUnit] = useState<ModelUnit>('mm')
  // What lengths are SHOWN in. Starts as the file's unit on every load; changing it
  // converts the readouts and leaves the model and its export exactly as they are.
  const [displayUnit, setDisplayUnit] = useState<ModelUnit>('mm')
  const [viewingScale, setViewingScale] = useState<number>(1)
  // Finer default chord error: at the old 0.03 the lower-wall blend regions still showed
  // triangular facets (most visible with a saturated color). 0.012 removes them while the
  // slider can still trade smoothness for vertex count. Paired with angularDeflection in
  // the STEP loader, which governs circumferential smoothness.
  const [linearDeflection, setLinearDeflection] = useState(0.012)
  const [uvProjection, setUvProjection] = useState<UvProjection>('cylindrical')
  const [vertexCount, setVertexCount] = useState(0)
  const [globalMaterial, setGlobalMaterial] = useState<MaterialProperties>(defaultMaterialProperties)
  const [showMeshOnSelect, setShowMeshOnSelect] = useState(false)
  const [openedFileName, setOpenedFileName] = useState<string | null>(null)
  // Orbit pivot. Held as a memoised tuple because r3f re-applies the prop whenever its
  // identity changes, which would undo an imperative camera move on the next render.
  const [cameraTarget, setCameraTarget] = useState<[number, number, number]>([0, 0, 0])
  const [focusRequest, setFocusRequest] = useState<{ position: THREE.Vector3; nonce: number } | null>(null)
  // Translation back to the source file's frame, so logo parts authored in preview
  // space re-export alongside the model (GLB only; STEP/STL have no faithful path).
  const [sourceOffset, setSourceOffset] = useState<[number, number, number] | undefined>(undefined)
  // Click-on-model placement for the selected logo.
  const [pickLogoMode, setPickLogoMode] = useState(false)
  // An in-progress "split at a height" on a two-tone part: which part, and where the
  // colour changes (model units, in the part's own geometry space, like every other length).
  const [splitPlan, setSplitPlan] = useState<{ id: string; height: number; picking: boolean } | null>(null)
  // A right-click in the 3D view, forwarded to the outliner's menu.
  const [menuRequest, setMenuRequest] = useState<{ id: string; x: number; y: number } | null>(null)

  // ---- Undo / redo history -------------------------------------------------
  const pastRef = useRef<HistorySnapshot[]>([])
  const futureRef = useRef<HistorySnapshot[]>([])
  // Tracks the last push so continuous edits (e.g. a slider drag) coalesce into
  // one undo step instead of one per pixel.
  const lastPushRef = useRef<{ coalesce: boolean; time: number }>({ coalesce: false, time: 0 })
  const [, setHistoryTick] = useState(0)
  const bumpHistory = useCallback(() => setHistoryTick((t) => t + 1), [])

  // Mirror of the current undoable state, refreshed every render so history
  // helpers always read the latest committed values.
  const liveRef = useRef<HistorySnapshot>({
    objects: [],
    globalMaterial: defaultMaterialProperties,
    selectedIds: [],
  })
  liveRef.current = { objects, globalMaterial, selectedIds }

  // Capture the current state before a mutation. `coalesce` groups rapid edits
  // (a slider drag) so only the pre-drag state is recorded.
  const pushHistory = useCallback(
    (coalesce = false) => {
      const now = Date.now()
      if (coalesce && lastPushRef.current.coalesce && now - lastPushRef.current.time < 700) {
        futureRef.current = [] // a fresh edit invalidates the redo stack
        lastPushRef.current.time = now
        return
      }
      pastRef.current.push(liveRef.current)
      if (pastRef.current.length > 100) pastRef.current.shift()
      futureRef.current = []
      lastPushRef.current = { coalesce, time: now }
      bumpHistory()
    },
    [bumpHistory]
  )

  const resetHistory = useCallback(() => {
    pastRef.current = []
    futureRef.current = []
    lastPushRef.current = { coalesce: false, time: 0 }
    bumpHistory()
  }, [bumpHistory])

  const applySnapshot = useCallback((s: HistorySnapshot) => {
    setObjects(s.objects)
    setGlobalMaterial(s.globalMaterial)
    setSelectedIds(s.selectedIds)
  }, [])

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return
    const prev = pastRef.current.pop()!
    futureRef.current.push(liveRef.current)
    applySnapshot(prev)
    lastPushRef.current = { coalesce: false, time: 0 }
    bumpHistory()
  }, [applySnapshot, bumpHistory])

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return
    const next = futureRef.current.pop()!
    pastRef.current.push(liveRef.current)
    applySnapshot(next)
    lastPushRef.current = { coalesce: false, time: 0 }
    bumpHistory()
  }, [applySnapshot, bumpHistory])

  const canUndo = pastRef.current.length > 0
  const canRedo = futureRef.current.length > 0

  // Ctrl/Cmd+Z to undo, Ctrl+Shift+Z or Ctrl+Y to redo. Ignored while typing in
  // an input (so the rename box keeps its own text undo).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      if (!(e.ctrlKey || e.metaKey)) return
      const k = e.key.toLowerCase()
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      } else if (k === 'y' || (k === 'z' && e.shiftKey)) {
        e.preventDefault()
        redo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo])

  // A splash left up by an in-flight load would outlive this tab, so clear it on the way out.
  useEffect(() => {
    return () => {
      store.loadingPage = false
    }
  }, [])

  // Strip the extension from the opened file name to use as an export base name.
  const exportBaseName = useMemo(() => {
    if (!openedFileName) return 'model'
    return openedFileName.replace(/\.[^.]+$/, '') || 'model'
  }, [openedFileName])

  // Detect if any object uses a glass/transparent material. A part without its own
  // material falls back to the global one, so test what each part actually renders with.
  const hasGlassMaterial = useMemo(
    () => objects.some((o) => (o.material ?? globalMaterial).transmission > 0),
    [objects, globalMaterial]
  )

  // Get the selected object
  const selectedObject = useMemo(
    () => objects.find((o) => o.id === selectedObjectId) || null,
    [objects, selectedObjectId]
  )

  // The selected part's geometry center (model units). Recomputed only when the
  // selected part's geometry changes, not when it's moved.
  const selectedBase = useMemo<[number, number, number]>(
    () => (selectedObject ? objectCenter(selectedObject) ?? [0, 0, 0] : [0, 0, 0]),
    [selectedObject?.mesh]
  )

  // Millimetres per model coordinate, and metres per model coordinate for the export.
  // Both come from the one declared unit. They used to be independent: this readout was
  // inferred from how big the model happened to be, while the export trusted the unit
  // named inside the STEP file. A file that declares inches but whose coordinates OCCT
  // has already normalised to millimetres made the two disagree, and the export came out
  // 25.4x too large while the panel read correctly.
  const positionMmScale = MODEL_UNIT_MM[modelUnit]
  const scaleToMeters = positionMmScale / 1000
  const units = useMemo(() => lengthUnits(modelUnit, displayUnit), [modelUnit, displayUnit])

  // The model's overall size, in model units, so the chosen unit can be sanity-checked
  // against something recognisable rather than trusted blind.
  const modelSize = useMemo(() => {
    let min = [Infinity, Infinity, Infinity]
    let max = [-Infinity, -Infinity, -Infinity]
    for (const o of objects) {
      const pos = o.mesh.attributes.position?.array
      if (!pos) continue
      const off = o.offset ?? [0, 0, 0]
      for (let i = 0; i < pos.length; i += 3) {
        for (let c = 0; c < 3; c++) {
          const v = pos[i + c] + off[c]
          if (v < min[c]) min[c] = v
          if (v > max[c]) max[c] = v
        }
      }
    }
    if (!Number.isFinite(min[0])) return null
    return max.map((v, i) => v - min[i]) as [number, number, number]
  }, [objects])

  // Part-id → geometry center Y, and the list of ids, for the pattern's "align to a part" option.
  const objectCenterYMap = useMemo(() => {
    const m: Record<string, number> = {}
    for (const o of objects) {
      const c = objectCenter(o)
      if (c) m[o.id] = c[1]
    }
    return m
  }, [objects])
  const logoObjId = useMemo(() => findLogoObject(objects)?.id, [objects])
  const objectIds = useMemo(() => objects.map((o) => o.id), [objects])

  // Selection with modifiers: replace (click), toggle (ctrl/cmd+click), range (shift+click).
  const handleSelect = useCallback(
    (id: string, mode: SelectMode = 'replace') => {
      setSelectedIds((prev) => {
        if (mode === 'toggle') {
          return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        }
        if (mode === 'range' && prev.length) {
          const order = objects.map((o) => o.id)
          const a = order.indexOf(prev[prev.length - 1])
          const b = order.indexOf(id)
          if (a < 0 || b < 0) return [id]
          const [lo, hi] = a < b ? [a, b] : [b, a]
          return Array.from(new Set([...prev, ...order.slice(lo, hi + 1)]))
        }
        return [id]
      })
    },
    [objects]
  )

  // Every selected part came with its own authored PBR surface. The pattern controls are
  // built on a generated cylindrical wrap, which such a part does not have and does not
  // need, so the panel drops them rather than offering an edit that cannot land correctly.
  const authoredSelection = useMemo(
    () => selectedIds.length > 0 && selectedIds.every((id) => !!objects.find((o) => o.id === id)?.sourceMaterial),
    [selectedIds, objects]
  )

  // The material shown in the panel: the active part's own, or the shared one it
  // falls back to.
  const activeMaterial = useMemo(() => selectedObject?.material ?? globalMaterial, [selectedObject, globalMaterial])

  // What a material edit will affect. Selection decides: with parts picked the edit is
  // theirs alone, with nothing picked it is the shared material every part without its
  // own follows. Previously per-part edits were only possible when the parts arrived
  // carrying materials — true of a GLB but never of a STEP — so on a STEP model every
  // colour change repainted the whole assembly.
  const materialScope = useMemo(() => {
    if (selectedIds.length === 0) return 'All parts'
    if (selectedIds.length === 1) return selectedIds[0]
    return `${selectedIds.length} selected parts`
  }, [selectedIds])

  // True when the selection has its own material and can be handed back to the shared one.
  const canResetMaterial = useMemo(
    () => selectedIds.some((id) => objects.find((o) => o.id === id)?.material !== undefined),
    [selectedIds, objects]
  )

  // Handle material changes from the panel
  const handleMaterialChange = useCallback(
    (newMaterial: MaterialProperties) => {
      pushHistory(true) // coalesce rapid edits (slider drags) into one undo step
      if (selectedIds.length > 0) {
        // Give every selected part its own copy, leaving the rest alone.
        const targets = new Set(selectedIds)
        setObjects((prev) => prev.map((o) => (targets.has(o.id) ? { ...o, material: newMaterial } : o)))
      } else {
        // Nothing picked: edit the shared material every un-styled part follows.
        setGlobalMaterial(newMaterial)
      }
    },
    [selectedIds, pushHistory]
  )

  // Drop a part's own material so it follows the shared one again — the way out of a
  // one-off colour without hunting back through undo.
  const handleResetMaterial = useCallback(() => {
    if (selectedIds.length === 0) return
    const targets = new Set(selectedIds)
    pushHistory(false)
    setObjects((prev) => prev.map((o) => (targets.has(o.id) ? { ...o, material: undefined } : o)))
  }, [selectedIds, pushHistory])

  /**
   * A logo remembers the part it is wrapped onto by id. Splitting, joining, renaming or
   * deleting that part leaves the reference dangling — and because a logo's geometry is
   * only rebuilt when its placement changes, nothing looks wrong until the user next
   * nudges it, at which point it re-anchors to whatever part is now largest and can land
   * at the wrong radius or inside the model. Every operation that changes what parts
   * exist re-points the logos that referred to them.
   */
  const retargetLogos = useCallback(
    (list: AppObject[], resolve: (targetId: string, logo: LogoPlacement) => string | undefined) =>
      list.map((o) => {
        if (!o.logo?.targetId) return o
        const next = resolve(o.logo.targetId, o.logo)
        return next === o.logo.targetId ? o : { ...o, logo: { ...o.logo, targetId: next } }
      }),
    []
  )

  // Rename a part (outliner). Keeps ids unique and moves the selection with it.
  const handleRenameObject = useCallback(
    (oldId: string, newName: string) => {
      const taken = new Set(objects.filter((o) => o.id !== oldId).map((o) => o.id))
      let unique = newName
      let n = 1
      while (taken.has(unique)) unique = `${newName}_${n++}`
      pushHistory(false)
      setObjects((prev) =>
        retargetLogos(
          prev.map((o) => (o.id === oldId ? { ...o, id: unique, mesh: { ...o.mesh, id: unique } } : o)),
          (targetId) => (targetId === oldId ? unique : targetId)
        )
      )
      setSelectedIds((prev) => prev.map((x) => (x === oldId ? unique : x)))
    },
    [objects, pushHistory, retargetLogos]
  )

  // Delete a part (outliner context menu). Removes it from the selection too.
  const handleDeleteObject = useCallback(
    (id: string) => {
      pushHistory(false)
      setObjects((prev) =>
        retargetLogos(
          prev.filter((o) => o.id !== id),
          (targetId) => (targetId === id ? undefined : targetId)
        )
      )
      setSelectedIds((prev) => prev.filter((x) => x !== id))
    },
    [pushHistory, retargetLogos]
  )

  // Move a part (position offset in model units). Coalesced so rapid nudges are one undo step.
  const handleMoveObject = useCallback(
    (id: string, offset: [number, number, number]) => {
      pushHistory(true)
      setObjects((prev) => prev.map((o) => (o.id === id ? { ...o, offset } : o)))
    },
    [pushHistory]
  )

  // Hide/show a part in the preview (view-only, still exported).
  const handleToggleHide = useCallback(
    (id: string) => {
      pushHistory(false)
      setObjects((prev) => prev.map((o) => (o.id === id ? { ...o, hidden: !o.hidden } : o)))
    },
    [pushHistory]
  )

  // Split a part into its disconnected pieces so each can be edited/moved on its own.
  const handleSplitObject = useCallback(
    (id: string) => {
      const obj = objects.find((o) => o.id === id)
      if (!obj) return
      const parts = splitObjectByConnectivity(obj)
      if (!parts || parts.length < 2) {
        alert('This part is a single connected piece — nothing to split.')
        return
      }
      pushHistory(false)
      setObjects((prev) => {
        const taken = new Set(prev.filter((o) => o.id !== id).map((o) => o.id))
        const unique = parts.map((p) => {
          let uid = p.id
          let k = 1
          while (taken.has(uid)) uid = `${p.id}_${k++}`
          taken.add(uid)
          return { ...p, id: uid, mesh: { ...p.mesh, id: uid } }
        })
        const i = prev.findIndex((o) => o.id === id)
        const next = [...prev.slice(0, i), ...unique, ...prev.slice(i + 1)]
        // No way to know which piece a logo belonged to, so hand it the one whose height
        // range contains it; failing that, let it fall back to automatic.
        return retargetLogos(next, (targetId, logo) => {
          if (targetId !== id) return targetId
          const piece = unique.find((p) => {
            const range = heightRangeOf(p)
            return range && logo.centerY >= range.min && logo.centerY <= range.max
          })
          return piece?.id
        })
      })
      setSelectedIds([])
    },
    [objects, pushHistory, retargetLogos]
  )

  // ---- Split a two-tone part in two ---------------------------------------
  // A body finished in two colours usually arrives as one mesh, so neither band can be
  // selected or given its own material. Cutting it at the colour line turns each into a
  // real part — which also means the upper band can be true metal, something a painted
  // pattern can never be.
  const splitPart = useMemo(
    () => (splitPlan ? objects.find((o) => o.id === splitPlan.id) : undefined),
    [splitPlan, objects]
  )

  // Wide enough to cut clear across the part from any angle.
  const splitGuideRadius = useMemo(() => {
    if (!splitPart) return 0
    const pos = splitPart.mesh.attributes.position?.array
    if (!pos) return 0
    let r = 0
    for (let i = 0; i < pos.length; i += 3) r = Math.max(r, Math.hypot(pos[i], pos[i + 2]))
    return r * 1.15
  }, [splitPart])

  const splitRange = useMemo(() => (splitPart ? heightRangeOf(splitPart) : null), [splitPart])

  const beginSplitByHeight = useCallback(
    (id: string) => {
      const obj = objects.find((o) => o.id === id)
      const range = obj ? heightRangeOf(obj) : null
      if (!obj || !range) return
      setSplitPlan({
        id,
        height: (range.min + range.max) / 2,
        picking: false,
      })
      setMeasureMode(false)
      setPickLogoMode(false)
      setSelectedIds([id])
    },
    [objects]
  )

  const confirmSplitByHeight = useCallback(() => {
    if (!splitPlan) return
    const obj = objects.find((o) => o.id === splitPlan.id)
    if (!obj) {
      setSplitPlan(null)
      return
    }
    const pieces = splitObjectByHeight(obj, splitPlan.height)
    if (!pieces) {
      alert('That height is outside the part — nothing to cut.')
      return
    }
    const taken = new Set(objects.filter((o) => o.id !== obj.id).map((o) => o.id))
    const unique = pieces.map((piece) => {
      let id = piece.id
      let n = 1
      while (taken.has(id)) id = `${piece.id}_${n++}`
      taken.add(id)
      return { ...piece, id, mesh: { ...piece.mesh, id } }
    })
    const cutHeight = splitPlan.height
    pushHistory(false)
    setObjects((prev) => {
      const i = prev.findIndex((o) => o.id === obj.id)
      const next = [...prev.slice(0, i), ...unique, ...prev.slice(i + 1)]
      // unique[0] is the upper half, unique[1] the lower.
      return retargetLogos(next, (targetId, logo) =>
        targetId === obj.id ? (logo.centerY >= cutHeight ? unique[0].id : unique[1].id) : targetId
      )
    })
    // Land on the upper band: it is usually the one wanting a different finish.
    setSelectedIds([unique[0].id])
    setSplitPlan(null)
  }, [splitPlan, objects, pushHistory, retargetLogos])

  // Join the selected parts into one mesh (so many split pieces become one movable part).
  const handleJoinObjects = useCallback(
    (ids: string[]) => {
      if (ids.length < 2) return
      // A logo part is regenerated from its placement, so merging it into another mesh
      // would be undone by the next placement edit.
      if (objects.some((o) => ids.includes(o.id) && o.logo)) {
        alert('A logo part cannot be joined — it is regenerated from its placement settings.')
        return
      }
      const idset = new Set(ids)
      const objs = objects.filter((o) => idset.has(o.id))
      const merged = joinObjects(objs)
      if (!merged) return
      const taken = new Set(objects.filter((o) => !idset.has(o.id)).map((o) => o.id))
      let uid = merged.id
      let k = 1
      while (taken.has(uid)) uid = `${merged.id}_${k++}`
      const mergedU: AppObject = { ...merged, id: uid, mesh: { ...merged.mesh, id: uid } }
      pushHistory(false)
      setObjects((prev) => {
        const out: AppObject[] = []
        let inserted = false
        for (const o of prev) {
          if (idset.has(o.id)) {
            if (!inserted) {
              out.push(mergedU)
              inserted = true
            }
          } else {
            out.push(o)
          }
        }
        return retargetLogos(out, (targetId) => (idset.has(targetId) ? uid : targetId))
      })
      setSelectedIds([uid])
    },
    [objects, pushHistory, retargetLogos]
  )

  // ---- Measurement (ruler) tool --------------------------------------------
  const [measureMode, setMeasureMode] = useState(false)
  const [measurePoints, setMeasurePoints] = useState<MeasurePoint[]>([])
  // Where a press would land right now (vertex-snapped), shown before the drag starts.
  const [measureHover, setMeasureHover] = useState<MeasurePoint | null>(null)

  // Measuring is a drag: press where the measurement starts, pull to where it ends, let
  // go. Two separate clicks made it easy to lose track of which end was which, and the
  // reading only appeared after the second one.
  const [measureDragging, setMeasureDragging] = useState(false)

  // Camera buttons. Normally left orbits, middle zooms, right pans. While measuring, left
  // drag draws the measurement, so orbit moves to the right button and pan is dropped; a
  // one-finger touch drag likewise measures, with two fingers still zooming and panning.
  // -1 is "no action" to OrbitControls. Memoised: r3f re-applies an object prop whenever its
  // identity changes.
  const orbitButtons = useMemo(
    () =>
      measureMode
        ? {
            mouse: { LEFT: -1 as THREE.MOUSE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE },
            touch: { ONE: -1 as THREE.TOUCH, TWO: THREE.TOUCH.DOLLY_PAN },
          }
        : {
            mouse: { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN },
            touch: { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN },
          },
    [measureMode]
  )

  const handleMeasureStart = useCallback((p: MeasurePoint) => {
    setMeasurePoints([p, { ...p, point: p.point.clone() }])
    setMeasureHover(null)
    setMeasureDragging(true)
  }, [])

  // The end follows the pointer across parts, so a measurement can start on one part and
  // finish on another; over empty space it keeps the last point it had.
  const handleMeasureMove = useCallback((p: MeasurePoint) => {
    setMeasurePoints((prev) => (prev.length === 2 ? [prev[0], p] : prev))
  }, [])

  // Ending on the window, not the mesh: a drag that runs off the model still has to
  // finish, and it should keep the last point it had rather than snapping back.
  useEffect(() => {
    if (!measureDragging) return
    const end = () => setMeasureDragging(false)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    return () => {
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
    }
  }, [measureDragging])

  // Distance between the two points and its per-axis parts, world → model units (shown in
  // the display unit). The axes are what a gap between two parts is usually wanted as: how
  // far apart vertically, not along the slant between two picked points.
  const measureDelta = useMemo(() => {
    if (measurePoints.length < 2) return null
    const d = new THREE.Vector3().subVectors(measurePoints[1].point, measurePoints[0].point)
    if (d.length() < 1e-6) return null
    return d.divideScalar(viewingScale > 0 ? viewingScale : 1)
  }, [measurePoints, viewingScale])

  const measureMid = useMemo(
    () =>
      measureDelta
        ? new THREE.Vector3().addVectors(measurePoints[0].point, measurePoints[1].point).multiplyScalar(0.5)
        : null,
    [measurePoints, measureDelta]
  )

  // ---- Logo parts ----------------------------------------------------------
  // A logo is a generated mesh that sits ON the model (see utils/logoMesh.ts) rather
  // than artwork printed into a body texture, so it can be picked, named and exported
  // as its own node.
  const logoInputRef = useRef<HTMLInputElement>(null)

  const handleAddLogo = useCallback(
    async (file: File | undefined) => {
      if (!file || objects.length === 0) return
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error('Could not read the image file'))
        reader.readAsDataURL(file)
      })
      // Measured and cropped to the visible mark first: a logo file is often an artboard
      // with the artwork somewhere inside it, and sizing the sheet from the file would be
      // sizing its empty margins.
      let artwork = { image: dataUrl, aspect: 1, knockoutWhite: false }
      try {
        artwork = await prepareLogoArtwork(dataUrl)
      } catch {
        // Unreadable image: fall back to a square, the user can resize it.
      }
      const id = nextLogoId(objects)
      // Trace the silhouette before the part is built: the sheet is cut to the artwork's
      // own outline, and the trace is what supplies that outline.
      await ensureLogoOutline(artwork.image, artwork.knockoutWhite)
      const placement = defaultLogoPlacement(
        objects,
        artwork.image,
        artwork.aspect,
        positionMmScale,
        artwork.knockoutWhite
      )
      pushHistory(false)
      setObjects((prev) => [...prev, buildLogoObject(id, placement, prev, sourceOffset)])
      setSelectedIds([id])
      setMeasureMode(false)
      setPickLogoMode(false)
    },
    [objects, positionMmScale, sourceOffset, pushHistory]
  )

  // Any placement change regenerates the sheet; coalesced so a slider drag is one undo step.
  const handleLogoChange = useCallback(
    async (id: string, placement: LogoPlacement) => {
      // Replaced artwork, or cut-to-shape just switched on, needs its outline traced first.
      if (placement.cutToShape) await ensureLogoOutline(placement.image, placement.knockoutWhite ?? false)
      pushHistory(true)
      setObjects((prev) =>
        prev.map((o) => (o.id === id && o.logo ? buildLogoObject(id, placement, prev, sourceOffset, o) : o))
      )
    },
    [pushHistory, sourceOffset]
  )

  // Click-to-place. One pick mode is live at a time, so the click is routed to whichever
  // asked for it: a logo's spot on the surface, or the height of a colour split.
  const handlePickPoint = useCallback(
    (point: THREE.Vector3) => {
      const model = point.clone().divideScalar(viewingScale || 1)

      if (splitPlan?.picking) {
        const part = objects.find((o) => o.id === splitPlan.id)
        // The click is in model space; the part's geometry sits under its move offset.
        const geometryY = model.y - (part?.offset?.[1] ?? 0)
        setSplitPlan({ ...splitPlan, height: geometryY, picking: false })
        return
      }

      if (!pickLogoMode) return
      const target = objects.find((o) => o.id === selectedObjectId)
      if (!target?.logo) return
      const spot = placementFromPoint(objects, target.logo, model)
      if (!spot) return
      handleLogoChange(target.id, { ...target.logo, ...spot })
      setPickLogoMode(false)
    },
    [objects, selectedObjectId, viewingScale, handleLogoChange, pickLogoMode, splitPlan]
  )

  const isPicking = pickLogoMode || !!splitPlan?.picking

  // Look straight at the selected logo — level with it and square on — so what the user
  // judges is the artwork rather than the viewing angle.
  const handleFaceLogo = useCallback(() => {
    const logoObj = objects.find((o) => o.id === selectedObjectId)
    if (!logoObj?.logo) return
    const center = objectCenter(logoObj)
    if (!center) return
    const targetPart = logoTargetOf(objects, logoObj.logo)
    const field = targetPart ? surfaceFieldOf(targetPart) : null
    const outward = new THREE.Vector3(center[0] - (field?.cx ?? 0), 0, center[2] - (field?.cz ?? 0))
    if (outward.lengthSq() < 1e-9) outward.set(0, 0, 1)
    outward.normalize()

    const { width, height } = logoObj.logo
    const distance = Math.max(width * 1.8, height * 6, outward.length() * 1.5)
    const focus = new THREE.Vector3(center[0], center[1], center[2]).multiplyScalar(viewingScale)
    setCameraTarget([focus.x, focus.y, focus.z])
    setFocusRequest({
      position: focus.clone().add(outward.multiplyScalar(distance * viewingScale)),
      nonce: (focusRequest?.nonce ?? 0) + 1,
    })
  }, [objects, selectedObjectId, viewingScale, focusRequest])

  const logoControls = useMemo(() => {
    if (!selectedObject?.logo) return undefined
    const id = selectedObject.id
    const placement = selectedObject.logo
    return {
      placement,
      onChange: (next: LogoPlacement) => handleLogoChange(id, next),
      name: id,
      onRename: (name: string) => handleRenameObject(id, name),
      targetIds: objects.filter((o) => !o.logo).map((o) => o.id),
      // How far the logo travels around the part. Past ~45deg its ends sit far enough
      // back that it reads as curved from any raised viewpoint, which is worth knowing
      // while sizing it rather than after exporting.
      wrapDeg: (() => {
        if (!placement.conform) return 0
        const targetPart = logoTargetOf(objects, placement)
        const field = targetPart ? surfaceFieldOf(targetPart) : null
        if (!field) return 0
        const radius = radiusAt(field, (placement.angleDeg * Math.PI) / 180, placement.centerY)
        return radius > 0 ? ((placement.width / radius) * 180) / Math.PI : 0
      })(),
      pickMode: pickLogoMode,
      onTogglePick: () => {
        setPickLogoMode((m) => !m)
        setMeasureMode(false)
      },
      onFace: handleFaceLogo,
    }
  }, [selectedObject, objects, pickLogoMode, handleLogoChange, handleRenameObject, handleFaceLogo])

  const handleLoadFromFile = async (files: File[]) => {
    // Remember the opened file name to suggest as the export name.
    setOpenedFileName(files[0]?.name ?? null)

    // The app-wide splash, same as the order page uses while it waits. Each loader
    // announces its phase here; because the heavy steps (OCCT tessellation above all)
    // are synchronous and freeze the page once they start, the loaders hand the browser
    // a frame to paint the message BEFORE beginning the work it describes — otherwise
    // nothing would appear at all until the model was already done.
    const onProgress = (progress: LoadProgress) => {
      store.loadingPage = progress.label
    }
    store.loadingPage = 'Opening your model…'

    // Check if all files are STL
    const allSTL = files.every((file) => file.name.toLowerCase().endsWith('.stl'))
    const isGLB =
      files.length === 1 &&
      (files[0].name.toLowerCase().endsWith('.glb') || files[0].name.toLowerCase().endsWith('.gltf'))

    if (allSTL) {
      try {
        const result = await loadSTLFiles(files, uvProjection, onProgress)
        bakePatternUVs(result.objects, uvProjection)
        setObjects(result.objects)
        resetHistory()
        setVertexCount(result.vertexCount)
        setViewingScale(result.viewingScale)
        setModelUnit(result.unit)
        setDisplayUnit(result.unit)
        setModelCenter(new THREE.Vector3(0, 0, 0))
        setCameraTarget([0, result.verticalCenter ?? 0, 0])
        setSourceOffset(result.sourceOffset)
      } catch (error) {
        alert(`Error loading STL files: ${error}`)
      } finally {
        store.loadingPage = false
      }
      return
    }

    if (isGLB) {
      try {
        const result = await loadGLBFile(files[0], uvProjection, onProgress)
        // A file that authored its own PBR maps addresses them through its own UV atlas.
        // The loader has already declined to project over it; snapping the dropdown to
        // None keeps the panel honest about that, and stops the cylindrical re-bake below
        // from overwriting the atlas on the way past.
        if (result.authoredMaterials) {
          setUvProjection('none')
        } else {
          bakePatternUVs(result.objects, uvProjection)
        }
        setObjects(result.objects)
        resetHistory()
        setVertexCount(result.vertexCount)
        setViewingScale(result.viewingScale)
        setModelUnit(result.unit)
        // A file exported from here comes back in the unit it was left in.
        setDisplayUnit(result.displayUnit ?? result.unit)
        setModelCenter(new THREE.Vector3(0, 0, 0))
        setCameraTarget([0, result.verticalCenter ?? 0, 0])
        setSourceOffset(result.sourceOffset)
        // Auto-select first object to show its material in the panel
        if (result.objects.length > 0) {
          setSelectedIds([result.objects[0].id])
        }
      } catch (error) {
        alert(`Error loading GLB/GLTF file: ${error}`)
      } finally {
        store.loadingPage = false
      }
      return
    }

    // For STEP files, only handle single file
    if (files.length > 1) {
      store.loadingPage = false
      alert('Multiple file upload is only supported for STL files. Please upload a single STEP or GLB file.')
      return
    }

    try {
      const result = await loadSTEPFile(files[0], linearDeflection, uvProjection, onProgress)
      bakePatternUVs(result.objects, uvProjection)
      setObjects(result.objects)
      resetHistory()
      setVertexCount(result.vertexCount)
      setViewingScale(result.viewingScale)
      setModelUnit(result.unit)
      setDisplayUnit(result.unit)
      setModelCenter(new THREE.Vector3(0, 0, 0))
      setCameraTarget([0, result.verticalCenter ?? 0, 0])
      setSourceOffset(result.sourceOffset)
    } catch (error) {
      alert(`Error loading STEP file: ${error}`)
    } finally {
      store.loadingPage = false
    }
  }

  const handleExportZip = async () => {
    if (objects.length === 0 || !modelCenter) return
    setIsExporting(true)
    setShowExportOptions(false)
    try {
      await exportAsZip(objects, scaleToMeters, globalMaterial, `${exportBaseName}.zip`, displayUnit)
    } finally {
      setIsExporting(false)
    }
  }

  const handleDownloadTemplate = () => {
    if (objects.length === 0) return
    const template = generatePatternTemplate(objects)
    if (!template) return
    downloadDataUrl(template.dataUrl, `${exportBaseName}-pattern-template.png`)
    if (!template.hasLogo) {
      alert(
        "No 'logo_main' part was found, so the template marks the front by convention (u=0.5) instead of the exact logo footprint."
      )
    }
  }

  const handleExportStl = async () => {
    if (objects.length === 0 || !modelCenter) return
    setIsExporting(true)
    setShowExportOptions(false)
    try {
      await exportAsSTL(objects, positionMmScale, `${exportBaseName}.stl`)
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportSingle = async () => {
    if (objects.length === 0 || !modelCenter) return
    setIsExporting(true)
    setShowExportOptions(false)
    try {
      await exportAsSingleGLB(objects, scaleToMeters, globalMaterial, `${exportBaseName}.glb`, displayUnit)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div
      className="relative w-full h-[calc(100vh-260px)] min-h-[520px] bg-white border border-gray-200 rounded-lg shadow-xs overflow-hidden flex flex-col"
      style={{ cursor: measureMode || isPicking ? 'crosshair' : undefined }}
    >
      {objects.length === 0 ? (
        <FileUploadBox
          onFileLoad={handleLoadFromFile}
          deflection={linearDeflection}
          setDeflection={setLinearDeflection}
          uvProjection={uvProjection}
          setUvProjection={setUvProjection}
        />
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex items-center gap-xs px-md py-sm border-b border-gray-200 bg-gray-25 flex-shrink-0">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                void handleAddLogo(e.target.files?.[0])
                e.target.value = '' // allow re-picking the same file
              }}
            />
            <ToolButton
              onClick={() => logoInputRef.current?.click()}
              variant="primary"
              icon={<Plus size={14} />}
              title="Add a logo to the model as its own named mesh part"
            >
              Logo
            </ToolButton>

            <ToolButton
              onClick={() => {
                setMeasureMode((m) => !m)
                setMeasurePoints([])
                setMeasureHover(null)
                setPickLogoMode(false)
              }}
              active={measureMode}
              icon={<Ruler size={14} />}
              title="Measure distance — drag from one point to another, across parts if needed. Snaps to vertices; hold Alt for a free point. Right-drag to turn the view, wheel to zoom."
            >
              Measure
            </ToolButton>
            {measureMode && measurePoints.length > 0 && (
              <ToolButton onClick={() => setMeasurePoints([])} title="Clear measurement points">
                Clear
              </ToolButton>
            )}

            <ToolDivider />

            <ToolButton onClick={undo} disabled={!canUndo} icon={<Undo2 size={14} />} title="Undo (Ctrl+Z)">
              Undo
            </ToolButton>
            <ToolButton onClick={redo} disabled={!canRedo} icon={<Redo2 size={14} />} title="Redo (Ctrl+Shift+Z)">
              Redo
            </ToolButton>

            <ToolDivider />

            <ToolButton
              onClick={handleDownloadTemplate}
              icon={<LayoutTemplate size={14} />}
              title="Download a proportioned artwork template with the logo alignment marked"
            >
              Template
            </ToolButton>

            <div className="ml-auto flex items-center gap-xs">
              <span className="text-text-xs text-gray-400 font-mono mr-xs hidden lg:inline">
                {vertexCount.toLocaleString()} verts
              </span>
              <div className="relative">
                <ToolButton
                  onClick={() => setShowExportOptions(!showExportOptions)}
                  disabled={isExporting}
                  variant="primary"
                  icon={<Download size={14} />}
                >
                  {isExporting ? 'Exporting…' : 'Export'}
                </ToolButton>
                {showExportOptions && (
                  <div className="absolute right-0 mt-xs min-w-[200px] bg-white border border-primary-500 shadow-focus rounded-md p-[4px] z-30">
                    <button
                      onClick={handleExportSingle}
                      className="w-full text-left px-md h-[37px] flex items-center text-text-md text-gray-700 rounded-sm hover:bg-primary-100 transition-all"
                    >
                      As a single GLB
                    </button>
                    <button
                      onClick={handleExportZip}
                      className="w-full text-left px-md h-[37px] flex items-center text-text-md text-gray-700 rounded-sm hover:bg-primary-100 transition-all"
                    >
                      As a ZIP of parts
                    </button>
                    <button
                      onClick={handleExportStl}
                      className="w-full text-left px-md h-[37px] flex items-center justify-between gap-sm text-text-md text-gray-700 rounded-sm hover:bg-primary-100 transition-all"
                      title="Geometry only, in millimetres — no colours, materials or part names"
                    >
                      As an STL
                      <span className="text-text-xs text-gray-400">shape only</span>
                    </button>
                  </div>
                )}
              </div>
              <ToolButton
                onClick={() => {
                  setObjects([])
                  setOpenedFileName(null)
                  setCameraTarget([0, 0, 0])
                  setSourceOffset(undefined)
                  setPickLogoMode(false)
                  setSplitPlan(null)
                  resetHistory()
                }}
                variant="danger"
                icon={<RotateCcw size={14} />}
                title="Clear the model and start again"
              >
                Reset
              </ToolButton>
            </div>
          </div>

          {/* Inspector | viewport | outliner */}
          <div className="flex-1 flex min-h-0">
            <MaterialSettingsPanel
              material={activeMaterial}
              onChange={handleMaterialChange}
              objectIds={objectIds}
              positionBase={selectedBase}
              positionOffset={selectedObject?.offset ?? [0, 0, 0]}
              onPositionChange={selectedObject ? (offset) => handleMoveObject(selectedObject.id, offset) : undefined}
              logoControls={logoControls}
              scopeLabel={materialScope}
              onResetMaterial={canResetMaterial ? handleResetMaterial : undefined}
              units={units}
              onDisplayUnitChange={setDisplayUnit}
              modelSize={modelSize}
              hasAuthoredMaterial={authoredSelection}
            />

            <div
              className="relative flex-1 min-w-0 bg-gray-100"
              // Right-drag orbits while measuring; releasing it must not open the browser menu.
              onContextMenu={measureMode ? (e) => e.preventDefault() : undefined}
            >
              {splitPlan && splitPart && splitRange && (
                <SplitHeightPanel
                  partId={splitPlan.id}
                  height={splitPlan.height}
                  min={splitRange.min}
                  max={splitRange.max}
                  units={units}
                  onHeightChange={(height) => setSplitPlan({ ...splitPlan, height })}
                  pickMode={splitPlan.picking}
                  onTogglePick={() => {
                    setSplitPlan({ ...splitPlan, picking: !splitPlan.picking })
                    setMeasureMode(false)
                    setPickLogoMode(false)
                  }}
                  onConfirm={confirmSplitByHeight}
                  onCancel={() => setSplitPlan(null)}
                />
              )}

              <div className="absolute z-10 bottom-md left-md bg-white border border-gray-200 rounded-md shadow-xs px-sm py-xs">
                <Checkbox
                  checked={showMeshOnSelect}
                  onChange={setShowMeshOnSelect}
                  label={<span className="text-text-sm">Show mesh on select</span>}
                />
              </div>

              <Canvas
                shadows
                camera={{ position: [100, 100, 150], fov: 45 }}
                gl={{
                  // Khronos PBR Neutral tone mapping preserves albedo hue/saturation,
                  // so printed pattern colors match the artwork file. ACES (the filmic
                  // default) pushes oranges/reds toward yellow and desaturates them.
                  toneMapping: THREE.NeutralToneMapping,
                  toneMappingExposure: hasGlassMaterial ? 1.2 : 1.0,
                }}
              >
                {/* The viewport sits on the portal's gray rather than a dark studio
                    backdrop, so the panel reads as one surface with the page. Exposure
                    and lighting still follow the material, though: brightening the
                    opaque case to match the glass case pushes light surfaces to clip at
                    white, and a clipped surface swallows any change to its colour. */}
                <color attach="background" args={['#F3F4F3']} />
                <Suspense fallback={<Html center>Loading…</Html>}>
                  <Environment preset="studio" />
                </Suspense>
                <ambientLight intensity={hasGlassMaterial ? 0.5 : 0.3} />
                <directionalLight
                  position={[10, 20, 5]}
                  intensity={hasGlassMaterial ? 2.0 : 2.5}
                  castShadow
                  shadow-mapSize-width={1024}
                  shadow-mapSize-height={1024}
                />
                {/* Fill light from the opposite side */}
                <directionalLight position={[-10, 10, -5]} intensity={hasGlassMaterial ? 0.8 : 0.3} />
                {/* In measure mode the left button belongs to the ruler, so the controls are
                    given no left-button action at all (see orbitButtons). Switching them off
                    once a measure drag began was too late: OrbitControls starts rotating on
                    the same pointerdown, before that state change renders, and a press that
                    missed the model never switched them off at all. */}
                <OrbitControls
                  makeDefault
                  enabled={!measureDragging}
                  target={cameraTarget}
                  mouseButtons={orbitButtons.mouse}
                  touches={orbitButtons.touch}
                />
                <CameraFocus request={focusRequest} />
                <group scale={viewingScale}>
                  {/* Soft contact shadows on the ground */}
                  <ContactShadows
                    position={[0, -0.01, 0]}
                    opacity={hasGlassMaterial ? 0.4 : 0.6}
                    scale={200}
                    blur={2}
                    far={100}
                  />
                  <group onPointerMissed={() => !measureMode && !isPicking && setSelectedIds([])} position={[0, 0, 0]}>
                    {objects.map((o) => {
                      const mat = o.material || globalMaterial
                      const refId = mat.patternVAlign === 'object' ? mat.patternVAlignRef || logoObjId : undefined
                      const alignRefCenterY = refId ? objectCenterYMap[refId] : undefined
                      return (
                        <ThreeObjectMesh
                          key={o.id}
                          object={o}
                          isSelected={selectedIds.includes(o.id)}
                          onSelect={() => handleSelect(o.id, 'replace')}
                          material={mat}
                          uvProjection={uvProjection}
                          showMeshOnSelect={showMeshOnSelect}
                          measureMode={measureMode}
                          onMeasureStart={handleMeasureStart}
                          onMeasureMove={handleMeasureMove}
                          onMeasureHover={setMeasureHover}
                          measureDragging={measureDragging}
                          pickMode={isPicking}
                          onPickPoint={handlePickPoint}
                          onContextMenu={(x, y) => {
                            if (!selectedIds.includes(o.id)) setSelectedIds([o.id])
                            setMenuRequest({ id: o.id, x, y })
                          }}
                          alignRefCenterY={alignRefCenterY}
                        />
                      )
                    })}
                  </group>
                </group>

                {/* Where the cut will land. Drawn inside the scaled group so it shares the
                    model's units, and generously oversized so it reads as a plane rather
                    than a disc floating on the surface. */}
                {splitPlan && splitPart && (
                  <group scale={viewingScale}>
                    <mesh
                      position={[0, splitPlan.height + (splitPart.offset?.[1] ?? 0), 0]}
                      rotation={[-Math.PI / 2, 0, 0]}
                      renderOrder={1001}
                    >
                      <ringGeometry args={[0, splitGuideRadius, 64]} />
                      <meshBasicMaterial
                        color={ACCENT}
                        transparent
                        opacity={0.35}
                        side={THREE.DoubleSide}
                        depthTest={false}
                      />
                    </mesh>
                  </group>
                )}

                {/* Measurement overlay (world space — points are captured in world coords).
                    The line is a dark core over a wider white halo so it reads on light and
                    black parts alike. Endpoints are screen-sized HTML markers: they stay the
                    same size at any zoom, never hide the point they mark, and never take the
                    pointer (pointerEvents none), so they cannot interrupt a drag. A square
                    marks a point snapped to a vertex, a dot a free one. */}
                {measurePoints.length === 2 && measureDelta && (
                  <>
                    <Line
                      points={[measurePoints[0].point, measurePoints[1].point]}
                      color={MEASURE_HALO}
                      lineWidth={5}
                      depthTest={false}
                      renderOrder={1000}
                    />
                    <Line
                      points={[measurePoints[0].point, measurePoints[1].point]}
                      color={MEASURE}
                      lineWidth={2}
                      depthTest={false}
                      renderOrder={1001}
                    />
                  </>
                )}
                {[
                  ...measurePoints.slice(0, measureDelta ? 2 : 1),
                  ...(measureMode && measureHover ? [measureHover] : []),
                ].map((p, i) => (
                  <Html key={i} position={p.point} center zIndexRange={[110, 0]} style={MEASURE_OVERLAY_STYLE}>
                    <MeasureMarker snapped={p.snapped} />
                  </Html>
                ))}
                {measureMid && measureDelta && (
                  <Html position={measureMid} center zIndexRange={[100, 0]} style={MEASURE_OVERLAY_STYLE}>
                    <div className="px-sm py-[2px] rounded-sm bg-gray-900 text-white font-mono whitespace-nowrap shadow-md ring-1 ring-white text-center">
                      <div className="text-text-xs">
                        {roundLength(measureDelta.length() * units.scale, units.unit)} {units.unit}
                      </div>
                      <div className="text-[10px] leading-[14px] text-gray-300">
                        {(['x', 'y', 'z'] as const)
                          .map(
                            (ax) =>
                              `${ax.toUpperCase()} ${roundLength(Math.abs(measureDelta[ax]) * units.scale, units.unit)}`
                          )
                          .join(' · ')}
                      </div>
                    </div>
                  </Html>
                )}
              </Canvas>
            </div>

            <ObjectTree
              objects={objects}
              selectedIds={selectedIds}
              onSelect={handleSelect}
              onRename={handleRenameObject}
              onDelete={handleDeleteObject}
              onToggleHide={handleToggleHide}
              onSplit={handleSplitObject}
              onSplitByHeight={beginSplitByHeight}
              menuRequest={menuRequest}
              onMenuRequestHandled={() => setMenuRequest(null)}
              onJoin={handleJoinObjects}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default StepToGlbConverter
